import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Input, InputNumber, Switch, Toast, Spin, Pagination } from '@douyinfe/semi-ui';
import { API, apiErrMsg } from '../helpers';
import { usePagedRows } from '../helpers/usePagedRows';

function fmtPrice(c) {
  if (!c) return '免费';
  return '￥' + (c / 100).toFixed(2);
}

const EMPTY_FORM = {
  name: '',
  price_cents: 990,    // 9.9 元
  total_calls: 10000,  // 10000 次
  limit_5h: 800,
  limit_week: 0,
  concurrency: 0,      // 单卡并发上限(0 = 该套餐不限并发)
  allowed_models: '',
  expired_days: 30,
  description: '',
  enabled: true,
};

const AdminPlans = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [defaultPlanId, setDefaultPlanId] = useState(0);

  // 窄屏(手机)用卡片视图替代表格:表格 10 列 + fixed 操作列在手机上会互相遮挡
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const [mobilePage, setMobilePage] = useState(1);

  // 桌面端表格分页:自己持有 currentPage,避免列表刷新时被 Semi 重置回第 1 页
  const TABLE_PAGE_SIZE = 20;
  const { page: tablePage, pagedList, setPage: setTablePage } = usePagedRows(list, TABLE_PAGE_SIZE);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const load = () => {
    setLoading(true);
    API.get('/api/admin/plans')
      .then((r) => setList(r.data.data || []))
      .catch((e) => Toast.error(apiErrMsg(e)))
      .finally(() => setLoading(false));
    // 同时拉设置,读默认套餐 id
    API.get('/api/admin/settings')
      .then((r) => setDefaultPlanId(parseInt(r.data.data?.default_token_plan_id || '0', 10) || 0))
      .catch(() => {});
  };
  useEffect(load, []);

  // 把某个套餐设为新建令牌的默认套餐
  const setAsDefault = async (row) => {
    try {
      await API.put('/api/admin/settings', { key: 'default_token_plan_id', value: String(row.id) });
      setDefaultPlanId(row.id);
      Toast.success('已把「' + row.name + '」设为新建令牌默认套餐');
    } catch (e) {
      Toast.error(e.response?.data?.message || '操作失败');
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name,
      price_cents: row.price_cents,
      total_calls: row.total_calls,
      limit_5h: row.limit_5h,
      limit_week: row.limit_week,
      concurrency: row.concurrency || 0,
      allowed_models: row.allowed_models || '',
      expired_days: row.expired_days == null ? -1 : row.expired_days,
      description: row.description || '',
      enabled: !!row.enabled,
    });
    setModalOpen(true);
  };

  const save = async () => {
    if (!form.name?.trim()) { Toast.error('请填写套餐名'); return; }
    setSaving(true);
    try {
      if (editing && editing.id) {
        await API.put(`/api/admin/plans/${editing.id}`, form);
        Toast.success('已保存');
      } else {
        await API.post('/api/admin/plans', form);
        Toast.success('已添加');
      }
      setModalOpen(false);
      load();
    } catch (e) {
      Toast.error(e.response?.data?.message || '操作失败');
    } finally {
      setSaving(false);
    }
  };

  const remove = (row) => {
    Modal.confirm({
      title: '删除套餐?',
      content: `将删除 ${row.name},此操作不可恢复`,
      onOk: () => API.delete(`/api/admin/plans/${row.id}`)
        .then(() => { Toast.success('已删除'); load(); })
        .catch((e) => Toast.error(apiErrMsg(e))),
    });
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: '套餐名', dataIndex: 'name', width: 180,
      render: (n, row) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span>{n}</span>
          {defaultPlanId === row.id && <span style={{ display: 'inline-block', padding: '1px 8px', borderRadius: 3, fontSize: 11, background: '#eaf2ff', color: '#1d7dfa', lineHeight: '16px', border: 'none', textDecoration: 'none' }}>默认</span>}
        </span>
      ),
    },
    { title: '价格', dataIndex: 'price_cents', width: 100, render: fmtPrice },
    { title: '月配额', dataIndex: 'total_calls', width: 100, render: (n) => n.toLocaleString() + ' 次' },
    {
      title: '5h 速率', dataIndex: 'limit_5h', width: 110,
      render: (n) => n ? n + ' 次/5h' : '不限',
    },
    {
      title: '本周速率', dataIndex: 'limit_week', width: 110,
      render: (n) => n ? n + ' 次/周' : '不限',
    },
    {
      // 单卡并发上限:这张卡同时在飞的请求数(按卡计,卡与卡互不影响)
      title: '并发', dataIndex: 'concurrency', width: 100,
      render: (n) => n
        ? <span style={{ color: '#1d7dfa', fontWeight: 500 }}>{n} 并发</span>
        : <span style={{ color: '#c9cdd4' }}>不限</span>,
    },
    {
      title: '模型', dataIndex: 'allowed_models', width: 180,
      render: (m) => m || <span style={{ color: '#c9cdd4' }}>全部</span>,
    },
    {
      title: '有效期', dataIndex: 'expired_days', width: 90,
      render: (d) => d == null || d < 0 ? '永久' : d + ' 天',
    },
    {
      title: '说明', dataIndex: 'description',
      render: (d) => d || <span style={{ color: '#c9cdd4' }}>—</span>,
    },
    {
      title: '状态', dataIndex: 'enabled', width: 80,
      render: (e) => e
        ? <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 4, fontSize: 12, background: '#e8ffea', color: '#00b42a', textDecoration: 'none', border: 'none', lineHeight: '18px' }}>上架</span>
        : <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 4, fontSize: 12, background: '#ffeaea', color: '#f56565', textDecoration: 'none', border: 'none', lineHeight: '18px' }}>下架</span>,
    },
    {
      title: '操作', width: 160, fixed: 'right',
      render: (_, row) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
          <Button size="small" theme="borderless" type="primary" style={{ padding: '0 6px' }} onClick={() => openEdit(row)}>编辑</Button>
          {defaultPlanId !== row.id && (
            <>
              <span style={{ color: '#c9cdd4', userSelect: 'none' }}>|</span>
              <Button size="small" theme="borderless" type="primary" style={{ padding: '0 6px' }} onClick={() => setAsDefault(row)}>默认</Button>
            </>
          )}
          <span style={{ color: '#c9cdd4', userSelect: 'none' }}>|</span>
          <Button size="small" theme="borderless" type="danger" style={{ padding: '0 6px' }} onClick={() => remove(row)}>删除</Button>
        </span>
      ),
    },
  ];

  const rowStyle = { display: 'flex', alignItems: 'flex-start', marginBottom: 12 };
  // form-row:窄屏下由 CSS 把 label + 输入框改为上下堆叠
  const rowClass = 'form-row';
  const labelCell = { width: 90, fontSize: 13, color: '#4e5969', paddingTop: 6, flexShrink: 0 };
  const inputCell = { flex: 1 };

  // 手机端卡片视图:每个套餐一张卡,操作按钮独占一行,不再被 fixed 列遮挡
  const renderMobileCards = () => {
    const PAGE_SIZE = 8;
    const totalPage = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
    const page = Math.min(mobilePage, totalPage);
    const pageRows = list.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const cell = (label, value, color) => (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: 12 }}>
        <span style={{ color: '#86909c', flexShrink: 0 }}>{label}</span>
        <span style={{ color: color || '#1f2329', textAlign: 'right', wordBreak: 'break-all' }}>{value}</span>
      </div>
    );
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {pageRows.map((row) => (
          <div key={row.id} style={{ border: '1px solid #e5e6eb', borderRadius: 8, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8, background: '#fff' }}>
            {/* 第一行:ID + 套餐名 + 默认标记 + 上下架 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: '#86909c' }}>#{row.id}</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#1f2329' }}>{row.name}</span>
              {defaultPlanId === row.id && (
                <span style={{ padding: '1px 8px', borderRadius: 3, fontSize: 11, background: '#eaf2ff', color: '#1d7dfa', lineHeight: '16px' }}>默认</span>
              )}
              <span style={{ flex: 1 }} />
              {row.enabled
                ? <span style={{ padding: '2px 10px', borderRadius: 4, fontSize: 12, background: '#e8ffea', color: '#00b42a', lineHeight: '18px' }}>上架</span>
                : <span style={{ padding: '2px 10px', borderRadius: 4, fontSize: 12, background: '#ffeaea', color: '#f56565', lineHeight: '18px' }}>下架</span>}
            </div>
            {/* 价格单独一行突出显示 */}
            <div style={{ fontSize: 18, fontWeight: 600, color: '#f56565' }}>{fmtPrice(row.price_cents)}</div>
            {/* 信息区:两列网格 */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px' }}>
              {cell('月配额', (row.total_calls || 0).toLocaleString() + ' 次')}
              {cell('有效期', row.expired_days == null || row.expired_days < 0 ? '永久' : row.expired_days + ' 天')}
              {cell('5h 速率', row.limit_5h ? row.limit_5h + ' 次/5h' : '不限')}
              {cell('本周速率', row.limit_week ? row.limit_week + ' 次/周' : '不限')}
            </div>
            {/* 模型限制 */}
            <div style={{ fontSize: 12 }}>
              <div style={{ color: '#86909c', marginBottom: 4 }}>模型限制</div>
              <div style={{ color: row.allowed_models ? '#1f2329' : '#c9cdd4', wordBreak: 'break-all' }}>{row.allowed_models || '全部'}</div>
            </div>
            {/* 说明 */}
            {row.description && (
              <div style={{ fontSize: 12 }}>
                <div style={{ color: '#86909c', marginBottom: 4 }}>说明</div>
                <div style={{ color: '#4e5969', wordBreak: 'break-all' }}>{row.description}</div>
              </div>
            )}
            {/* 操作区:整行铺开 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', borderTop: '1px solid #f2f3f5', paddingTop: 8 }}>
              <Button size="small" theme="borderless" type="primary" style={{ padding: '0 6px' }} onClick={() => openEdit(row)}>编辑</Button>
              {defaultPlanId !== row.id && (
                <>
                  <span style={{ color: '#c9cdd4', userSelect: 'none' }}>|</span>
                  <Button size="small" theme="borderless" type="primary" style={{ padding: '0 6px' }} onClick={() => setAsDefault(row)}>默认</Button>
                </>
              )}
              <span style={{ color: '#c9cdd4', userSelect: 'none' }}>|</span>
              <Button size="small" theme="borderless" type="danger" style={{ padding: '0 6px' }} onClick={() => remove(row)}>删除</Button>
            </div>
          </div>
        ))}
        {list.length > PAGE_SIZE && (
          <Pagination
            total={list.length}
            pageSize={PAGE_SIZE}
            currentPage={page}
            onPageChange={(p) => setMobilePage(p)}
            size="small"
            style={{ alignSelf: 'center' }}
          />
        )}
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>套餐管理</h2>
        <Button type="primary" theme="solid" onClick={openNew}>+ 新建套餐</Button>
      </div>

      <div style={{ background: '#fff', borderRadius: 10, padding: 8, border: '1px solid #e5e6eb' }}>
        <Spin spinning={loading}>
          {isMobile ? (
            // 手机端:卡片视图。表格 10 列 + fixed 操作列在窄屏会互相遮挡
            renderMobileCards()
          ) : (
            <Table size="small" rowKey="id" columns={columns} dataSource={pagedList} scroll={{ x: 1080 }}
              pagination={{
                currentPage: tablePage,
                pageSize: TABLE_PAGE_SIZE,
                total: list.length,
                hideOnSinglePage: true,
                onPageChange: setTablePage,
              }} />
          )}
        </Spin>
      </div>

      <Modal
        title={editing && editing.id ? '编辑套餐' : '新建套餐'}
        visible={modalOpen}
        onCancel={() => setModalOpen(false)}
        width={560}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
            <Button onClick={() => setModalOpen(false)}>取消</Button>
            <Button type="primary" theme="solid" onClick={save} loading={saving}>保存</Button>
          </div>
        }
      >
        <div style={{ padding: '8px 0' }}>
          <div className={rowClass} style={rowStyle}>
            <label style={labelCell}>套餐名 <span style={{ color: '#f56565' }}>*</span></label>
            <div style={inputCell}>
              <Input value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="例如:基础 9.9" />
            </div>
          </div>
          <div className={rowClass} style={rowStyle}>
            <label style={labelCell}>价格(元)</label>
            <div style={inputCell}>
              <InputNumber
                min={0} step={0.1} precision={2}
                value={form.price_cents / 100}
                onChange={(v) => setForm({ ...form, price_cents: Math.round(Number(v || 0) * 100) })}
                formatter={(v) => '￥' + v}
                parser={(v) => v.replace(/￥\s*/g, '')}
                style={{ width: '100%' }}
              />
              <div className="form-hint">单位:元(支持两位小数,如 9.9 = 9.9 元/月)</div>
            </div>
          </div>
          <div className={rowClass} style={rowStyle}>
            <label style={labelCell}>月配额</label>
            <div style={inputCell}>
              <InputNumber min={0} step={100} value={form.total_calls} onChange={(v) => setForm({ ...form, total_calls: v })} style={{ width: '100%' }} />
              <div className="form-hint">单位:次(月度总调用次数上限)</div>
            </div>
          </div>
          <div className={rowClass} style={rowStyle}>
            <label style={labelCell}>5 小时速率</label>
            <div style={inputCell}>
              <InputNumber min={0} step={50} value={form.limit_5h} onChange={(v) => setForm({ ...form, limit_5h: v })} style={{ width: '100%' }} />
              <div className="form-hint">0 = 不限,单位:次/5h</div>
            </div>
          </div>
          <div className={rowClass} style={rowStyle}>
            <label style={labelCell}>本周速率</label>
            <div style={inputCell}>
              <InputNumber min={0} step={100} value={form.limit_week} onChange={(v) => setForm({ ...form, limit_week: v })} style={{ width: '100%' }} />
              <div className="form-hint">0 = 不限,单位:次/周</div>
            </div>
          </div>
          <div className={rowClass} style={rowStyle}>
            <label style={labelCell}>并发上限</label>
            <div style={inputCell}>
              <InputNumber min={0} max={200} step={1} value={form.concurrency} onChange={(v) => setForm({ ...form, concurrency: Math.max(0, Number(v) || 0) })} style={{ width: '100%' }} />
              <div className="form-hint">这张卡允许<b>同时在飞</b>的请求数上限。超出的请求会先排队等空位,排队超时(USER_QUEUE_TIMEOUT_S,默认 120 秒)才返回 429。0 = 该套餐不限并发</div>
            </div>
          </div>
          <div className={rowClass} style={rowStyle}>
            <label style={labelCell}>模型限制</label>
            <div style={inputCell}>
              <Input value={form.allowed_models} onChange={(v) => setForm({ ...form, allowed_models: v })} placeholder="留空=全部模型,逗号分隔,如 MiniMax-M3,MiniMax-M2.7" />
            </div>
          </div>
          <div className={rowClass} style={rowStyle}>
            <label style={labelCell}>有效期(天)</label>
            <div style={inputCell}>
              <InputNumber min={-1} step={1} value={form.expired_days} onChange={(v) => setForm({ ...form, expired_days: v })} style={{ width: '100%' }} />
              <div className="form-hint">-1 = 永不过期,单位:天(从生成令牌起算)</div>
            </div>
          </div>
          <div className={rowClass} style={rowStyle}>
            <label style={labelCell}>说明</label>
            <div style={inputCell}>
              <Input value={form.description} onChange={(v) => setForm({ ...form, description: v })} placeholder="可选,例如 9.9 元/月,10000 次调用" />
            </div>
          </div>
          <div className={rowClass} style={rowStyle}>
            <label style={labelCell}>上架</label>
            <div style={inputCell}>
              <Switch checked={form.enabled} onChange={(v) => setForm({ ...form, enabled: v })} />
              <span style={{ marginLeft: 8, fontSize: 12, color: '#86909c' }}>{form.enabled ? '上架中' : '已下架'}</span>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminPlans;